import { useQuery } from '@tanstack/react-query';
import { billingService, queryKeys } from '@/services';

/**
 * Month-to-date cash pool interest preview for one counterparty (LR-624).
 * Disabled until a counterparty is selected. Same endpoint as the trade
 * preview with `statement_type=cash_pool_interest` — the param is part of the
 * cache key, so the two previews never overwrite each other's entry.
 */
export const useCashPoolPreview = (params: { counterparty: number } | null) => {
  return useQuery({
    // The discriminator rides in the key even when disabled. Without it
    // `previewFor(undefined)` collapses onto the trade hook's disabled key, so
    // two different `queryFn`s register against one cache entry and the first
    // `fetchQuery` / `invalidateQueries({ refetchType: 'all' })` on
    // `billingKeys.preview()` would resolve it with whichever was registered
    // first. Inert today (both are disabled at that key) - and a trap with no
    // upside, since the fix is one spread.
    queryKey: queryKeys.billing.previewFor({
      ...(params ?? {}),
      statement_type: 'cash_pool_interest',
    }),
    queryFn: () =>
      billingService.getCashPoolPreview(params as { counterparty: number }),
    enabled: params !== null && Number.isFinite(params.counterparty),
    // Live read of unattached accruals - don't serve a stale cache on reopen.
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};
